import { SerializedStyles } from "@emotion/react";
import { Ref, memo, useCallback } from "react";
import { useNoSubmit } from "src/apps/AppFlow/pages/hooks";
import { styles } from "src/apps/AppFlow/pages/styles";
import { FormFlowPage, TextInput } from "src/components";
import { MortgageBalanceAndLenderValue } from "src/types/Application";
import { noInput } from "src/utilities/input";

export interface MortgagePaymentInformationProps {
  onChange: (value: MortgageBalanceAndLenderValue) => void;
  value?: MortgageBalanceAndLenderValue;
  propertyAddress?: string;

  focusRef?: Ref<HTMLInputElement>;
  onNext: () => void;
  onBack: () => void;
  style?: SerializedStyles;
}

const MortgageBalanceAndLender = ({
  onNext,
  onBack,
  style,
  focusRef,
  value,
  propertyAddress,
  onChange,
  ...props
}: MortgagePaymentInformationProps) => {
  const validate = useCallback(() => !noInput(value?.amount) && !noInput(value?.lenderName), [value]);
  const { handleNext, nextDisabled } = useNoSubmit({ onNext, validate });

  const handleChangeAmount = useCallback(
    (amount?: string) => {
      onChange?.({
        ...value,
        amount,
      });
    },
    [onChange, value]
  );

  const handleChangeLenderName = useCallback(
    (lenderName?: string) => {
      onChange?.({
        ...value,
        lenderName,
      });
    },
    [onChange, value]
  );

  return (
    <FormFlowPage
      {...props}
      title="Mortgage balance & lender"
      description={`What is the current balance and the name of the lender for the mortgage or HELOC at ${
        propertyAddress || "your current property"
      }?`}
      onNext={handleNext}
      onBack={onBack}
      style={style}
      nextDisabled={nextDisabled}
    >
      <TextInput
        ref={focusRef}
        style={styles.topInput}
        name="amount"
        label="Current mortgage balance"
        value={value?.amount || ""}
        onChange={handleChangeAmount}
        min={0}
        max={100000000}
        maskOptions={{
          rightAlign: false,
          alias: "currency",
          prefix: "$ ",
          jitMasking: false,
          digits: 0,
        }}
      />
      <TextInput
        name="lenderName"
        label="Lender name"
        value={value?.lenderName || ""}
        onChange={handleChangeLenderName}
      />
    </FormFlowPage>
  );
};

export default memo(MortgageBalanceAndLender);
